import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  exportCompanySalesReport,
  getAllBranch,
  getCompanySalesReport,
} from "../../services";
import { Branch } from "../../types/user.type";
import {
  CompanySalesFigures,
  CompanySalesGroupBy,
  CompanySalesReport as CompanySalesReportData,
  CompanySalesSplit,
} from "../../types/companySalesReport.type";
import { baht, pcs, todayBkk } from "../Bdc/format";
import { CARD_SX, SCROLL_SX, TABLE_SX } from "../Bdc/styles";
import { useBlobExport } from "../Bdc/useBlobExport";
import {
  ALL_BRANCHES,
  branchLabel,
  groupRowsByBranch,
  periodLabel,
  rangeErrorOf,
  reportParams,
} from "./report";

const COLUMN_COUNT = 14;

/**
 * The figure cells of one row: vansale (bills, sales, CN, DN, net), BDC (the same without DN,
 * which only Vansale has), then the sum.
 */
function FigureCells({
  split,
  strong = false,
}: {
  split: CompanySalesSplit;
  strong?: boolean;
}) {
  const cell = (value: number) => (strong ? <b>{baht(value)}</b> : baht(value));
  const channel = (figures: CompanySalesFigures, withDebitNote = false) => (
    <>
      <td className="n">{pcs(figures.invoice_count)}</td>
      <td className="n">{cell(figures.invoice_subtotal)}</td>
      <td className="n neg">
        {figures.cn_subtotal ? `-${baht(figures.cn_subtotal)}` : "-"}
      </td>
      {withDebitNote && (
        <td className="n">
          {figures.dn_subtotal ? baht(figures.dn_subtotal) : "-"}
        </td>
      )}
      <td className="n">{cell(figures.net_subtotal)}</td>
    </>
  );
  return (
    <>
      {channel(split.vansale, true)}
      {channel(split.bdc)}
      <td className="n">
        <b>{baht(split.total.net_subtotal)}</b>
      </td>
      <td className="n">{baht(split.total.net_vat)}</td>
      <td className="n">{cell(split.total.net_total)}</td>
    </>
  );
}

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card sx={{ ...CARD_SX, p: 2, flex: "1 1 200px", minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Card>
  );
}

/**
 * รายงานยอดขายทั้งบริษัท (HO) — BDC plan §15 option A step 3 under §17 L4: sales are the issued
 * documents (vansale bills + BDC INV) minus credit notes plus debit notes, before VAT, by document
 * date, read by the server from the unified views only. Per branch × day / month with the
 * vansale / BDC split.
 */
export default function CompanySalesReport() {
  const today = todayBkk();
  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [groupBy, setGroupBy] = useState<CompanySalesGroupBy>("month");
  const [companyId, setCompanyId] = useState<string>(ALL_BRANCHES);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [report, setReport] = useState<CompanySalesReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const { busy, run } = useBlobExport();

  const rangeError = rangeErrorOf(dateFrom, dateTo);
  const params = useMemo(
    () => reportParams({ dateFrom, dateTo, groupBy, companyId }),
    [dateFrom, dateTo, groupBy, companyId],
  );

  useEffect(() => {
    getAllBranch()
      .then(({ data }) => setBranches((data?.data as Branch[]) ?? []))
      .catch(() => setBranches([]));
  }, []);

  const load = useCallback(async () => {
    if (rangeError) return;
    setLoading(true);
    try {
      const { data } = await getCompanySalesReport(params);
      setReport(data?.data ?? null);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [params, rangeError]);

  useEffect(() => {
    load();
  }, [load]);

  const pickDate =
    (setter: (value: string) => void) => (value: Dayjs | null) => {
      if (value && value.isValid()) setter(value.format("YYYY-MM-DD"));
    };

  const groups = useMemo(() => groupRowsByBranch(report?.rows ?? []), [report]);
  const branchTotals = useMemo(
    () =>
      new Map(
        (report?.branches ?? []).map((branch) => [branch.company_id, branch]),
      ),
    [report],
  );
  const totals = report?.totals;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            ยอดขายทั้งบริษัท
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          disabled={Boolean(rangeError)}
          loading={busy === "company-sales"}
          onClick={() =>
            run(
              "company-sales",
              () => exportCompanySalesReport(params),
              `company-sales-${dateFrom}-${dateTo}.xlsx`,
            )
          }
        >
          Export Excel
        </Button>
      </Stack>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack
              direction="row"
              gap={1.5}
              flexWrap="wrap"
              alignItems="center"
            >
              <TextField
                select
                size="small"
                label="สาขา"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                sx={{ minWidth: 220 }}
                slotProps={{
                  htmlInput: { "data-testid": "company-sales-branch" },
                }}
              >
                <MenuItem value={ALL_BRANCHES}>ทุกสาขา</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.company_id} value={branch.company_id}>
                    {branch.branch_code} {branch.branch_name}
                  </MenuItem>
                ))}
              </TextField>
              <DatePicker
                label="วันที่เอกสาร ตั้งแต่"
                value={dayjs(dateFrom)}
                format="DD/MM/YYYY"
                onChange={pickDate(setDateFrom)}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <DatePicker
                label="ถึง"
                value={dayjs(dateTo)}
                format="DD/MM/YYYY"
                onChange={pickDate(setDateTo)}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 170 },
                    error: Boolean(rangeError),
                  },
                }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={groupBy}
                onChange={(_e, value: CompanySalesGroupBy | null) =>
                  value && setGroupBy(value)
                }
              >
                <ToggleButton value="day">รายวัน</ToggleButton>
                <ToggleButton value="month">รายเดือน</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </LocalizationProvider>
        </Card>

        {rangeError && <Alert severity="error">{rangeError}</Alert>}
        {loading && <LinearProgress />}

        {totals && (
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            <Kpi
              label="ยอดขายสุทธิ ก่อน VAT"
              value={`฿${baht(totals.total.net_subtotal)}`}
              sub={`VAT ฿${baht(totals.total.net_vat)} · รวม ฿${baht(totals.total.net_total)}`}
            />
            <Kpi
              label="Vansale สุทธิ"
              value={`฿${baht(totals.vansale.net_subtotal)}`}
              sub={`${pcs(totals.vansale.invoice_count)} บิล`}
            />
            <Kpi
              label="BDC สุทธิ"
              value={`฿${baht(totals.bdc.net_subtotal)}`}
              sub={`${pcs(totals.bdc.invoice_count)} INV`}
            />
            <Kpi
              label="หัก CN ก่อน VAT"
              value={`฿${baht(totals.total.cn_subtotal)}`}
              sub={`${pcs(totals.total.cn_count)} ใบ`}
            />
            <Kpi
              label="บวก DN ก่อน VAT"
              value={`฿${baht(totals.total.dn_subtotal)}`}
              sub={`${pcs(totals.total.dn_count)} ใบ`}
            />
          </Stack>
        )}

        <Card sx={CARD_SX}>
          <Box sx={SCROLL_SX}>
            <table className="bdc-t" data-testid="company-sales-table">
              <thead>
                <tr>
                  <th rowSpan={2}>สาขา</th>
                  <th rowSpan={2}>
                    {groupBy === "month" ? "เดือน" : "วันที่"}
                  </th>
                  <th colSpan={5} style={{ textAlign: "center" }}>
                    Vansale
                  </th>
                  <th colSpan={4} style={{ textAlign: "center" }}>
                    BDC
                  </th>
                  <th colSpan={3} style={{ textAlign: "center" }}>
                    รวม
                  </th>
                </tr>
                <tr>
                  <th className="n">บิล</th>
                  <th className="n">ยอดบิล</th>
                  <th className="n">CN</th>
                  <th className="n">DN</th>
                  <th className="n">สุทธิ</th>
                  <th className="n">INV</th>
                  <th className="n">ยอด INV</th>
                  <th className="n">CN</th>
                  <th className="n">สุทธิ</th>
                  <th className="n">สุทธิ ก่อน VAT</th>
                  <th className="n">VAT</th>
                  <th className="n">รวม</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  const branchTotal = branchTotals.get(group.company_id);
                  return (
                    <Fragment key={group.company_id}>
                      {group.rows.map((row, index) => (
                        <tr key={`${row.company_id}-${row.period}`}>
                          <td>
                            {index === 0 ? <b>{branchLabel(row)}</b> : ""}
                          </td>
                          <td>{periodLabel(row.period, groupBy)}</td>
                          <FigureCells split={row} />
                        </tr>
                      ))}
                      {group.rows.length > 1 && branchTotal && (
                        <tr
                          className="subtotal"
                          style={{ background: "#F7F8FA" }}
                        >
                          <td />
                          <td>
                            <b>รวมสาขา</b>
                          </td>
                          <FigureCells split={branchTotal} strong />
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {groups.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={COLUMN_COUNT}
                      className="sub"
                      style={{ textAlign: "center", padding: 24 }}
                    >
                      ไม่มียอดขายในช่วงนี้
                    </td>
                  </tr>
                )}
              </tbody>
              {totals && groups.length > 0 && (
                <tfoot>
                  <tr
                    data-testid="company-sales-total"
                    style={{ background: "#FFF3DC" }}
                  >
                    <td colSpan={2}>
                      <b>รวมทั้งหมด</b>
                    </td>
                    <FigureCells split={totals} strong />
                  </tr>
                </tfoot>
              )}
            </table>
          </Box>
        </Card>
      </Stack>
    </Box>
  );
}
